import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";
import { useRuntimeConfig } from "@/hooks/useRuntimeConfig";

const Register = () => {
  const { t, lang, pickI18n, tProfile, tCountry } = useLang();
  const runtimeCfg = useRuntimeConfig();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  // The landing-page profile cards link here with `?profile=<key>`. When that
  // query param is present we lock the field to that profile (read-only chip
  // + "Change profile" link back to /#profiles) so the user cannot
  // accidentally switch profile after committing on the landing card.
  // Direct visits without the param keep the dropdown fallback.
  const requestedProfile = params.get("profile") || "";
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    profile_key: requestedProfile,
    country: "",
    language: lang,
    is_minor: false,
    guardian_email: "",
    consent_terms: false,
    consent_privacy: false,
    research_consent: false,
    crm_contact_consent: false,
    commercial_communications_consent: false,
    pilot_participation_consent: false,
    // PREVIEW-SIGNUP-APP-SELECTION-CHOICE-1 (Feb 2026) — user picks the
    // scope of their registration. Default "questionnaires" preserves
    // the prior hardcoded behaviour. "racket_lab" auto-includes
    // Cuestionarios (Master Rule RL → Q) and only succeeds if the
    // backend env gate PUBLIC_REGISTRATION_RACKET_LAB_ENABLED is true.
    selected_app: "questionnaires",
  });

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data));
    api.get("/countries").then((r) => setCountries(r.data));
    // QA: Registration must always open at the top of the form, not wherever
    // the user was scrolled on the landing page. Run on mount only.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    setForm((f) => ({ ...f, language: lang }));
  }, [lang]);

  // Country dropdown is alphabetical by the localized name. Re-sorts on
  // language switch.
  const sortedCountries = React.useMemo(() => {
    const collator = new Intl.Collator(lang || "en", { sensitivity: "base" });
    return [...countries].sort((a, b) => {
      const an = tCountry(a.code, a);
      const bn = tCountry(b.code, b);
      return collator.compare(an, bn);
    });
  }, [countries, lang, tCountry]);

  // Profile is "locked" only when the URL requested a profile AND that
  // profile actually exists in the public registry. An unknown / stale key
  // falls back to the dropdown so the user can still recover.
  const lockedProfile = requestedProfile
    ? profiles.find((p) => p.key === requestedProfile) || null
    : null;
  const isProfileLocked = !!lockedProfile;

  // Inline error state for the parent/tutor email field. We intentionally
  // validate only when Player + is_minor=true (the only case the field is
  // actually shown). The message matches the friendly copy from QA.
  const [guardianError, setGuardianError] = useState("");
  const guardianRef = useRef(null);
  const guardianMessage = () => t("phase2.guardianEmailInvalid");
  // Simple RFC-5322-lite shape check. The backend's Pydantic EmailStr is the
  // authoritative validator; this client-side guard is purely to present an
  // inline error + focus before the round-trip. Any server 422 still falls
  // through to the friendly toast via extractErrorMessage.
  const isValidEmailShape = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "guardian_email" && guardianError) setGuardianError("");
    if (k === "is_minor" && !v) setGuardianError("");
  };

  // The "I am under 18" checkbox + guardian email are only meaningful for the
  // public Player registration flow. Switching to any other profile must also
  // clear is_minor and guardian_email so a stale value can never be submitted.
  // NOTE: questionnaire-level sensitive tags (minor/health/injury/etc.) are
  // research data classification and intentionally NOT used here.
  const isMinorEligibleProfile = form.profile_key === "player";
  const handleProfileChange = (next) => {
    setForm((f) => ({
      ...f,
      profile_key: next,
      ...(next === "player" ? {} : { is_minor: false, guardian_email: "" }),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_terms || !form.consent_privacy) {
      toast.error(t("phase2.mustAcceptTermsPrivacy"));
      return;
    }
    if (form.password.length < 8) {
      toast.error(t("phase2.passwordMinReg"));
      return;
    }
    // Client-side guard: Player + minor requires a valid guardian email.
    // Show an inline message under the field and focus/scroll to it instead
    // of surfacing the server 422 as a top-right toast.
    if (form.profile_key === "player" && form.is_minor && !isValidEmailShape(form.guardian_email)) {
      setGuardianError(guardianMessage());
      // defer focus/scroll until after React paints the error message
      requestAnimationFrame(() => {
        const el = guardianRef.current;
        if (el) {
          try { el.focus({ preventScroll: true }); } catch { el.focus(); }
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      return;
    }
    setLoading(true);
    try {
      // Defense-in-depth: never submit is_minor/guardian_email for non-player
      // profiles even if the local state somehow held stale values. Adult
      // players (is_minor=false) must send `null` for guardian_email because
      // Pydantic `Optional[EmailStr]` rejects `""` with a 422 and the inline
      // error DIV isn't mounted unless is_minor is true — which would cause a
      // silent submit with no visible feedback.
      const normalizedGuardian =
        form.profile_key === "player" && form.is_minor && form.guardian_email
          ? form.guardian_email
          : null;
      const payload = form.profile_key === "player"
        ? { ...form, guardian_email: normalizedGuardian }
        : { ...form, is_minor: false, guardian_email: null };
      // PREVIEW-SIGNUP-APP-SELECTION-CHOICE-1 (Feb 2026) — registration
      // context now mirrors the user's app choice. The Master Rule
      // (Racket Lab → Questionnaires) is enforced server-side by the
      // env-scoped gates + per-context whitelist; for closed scopes the
      // backend returns errors.registration_context_disabled which the
      // friendly toast surfaces via extractErrorMessage. Default
      // "questionnaires" preserves backwards compatibility for any
      // legacy click-paths that bypass the radio.
      payload.registration_context = form.selected_app || "questionnaires";
      // A19-P1.3 — forward a referral token from /r/:token (or any deep link
      // that included ?token=...) so the backend can link this new user
      // back to the inviter's invitation row.
      const refTok = params.get("token");
      if (refTok) payload.referral_token = refTok;
      const data = await register(payload);
      // In MOCK MODE the backend returns the verification token so QA can
      // complete the flow without real email delivery. We surface it in a
      // sticky toast (with a clickable link) AND keep the user on /app
      // where the verify-pending banner will also show it. Production /
      // Resend live mode never returns this token, so the banner is hidden.
      if (data?.email_mock_mode && data?.dev_verify_url) {
        sessionStorage.setItem("tsw_dev_verify_url", data.dev_verify_url);
        toast.success(
          `${t("phase2.mockAccountCreatedPrefix")} ${data.dev_verify_url}`,
          { duration: 12000 }
        );
      } else {
        toast.success(t("phase2.accountCreated"));
      }
      // EVH-1 (Feb 2026) — when the backend issued a soft-login token,
      // route the brand-new account straight to /verify-email-pending
      // instead of the dashboard. Without verification, /app would
      // 401 on every authenticated call and bounce them back to /login.
      if (data?.soft_login === true) {
        navigate("/verify-email-pending");
      } else {
        navigate("/app");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const guardianPydanticErr = Array.isArray(detail)
        ? detail.find((d) => Array.isArray(d?.loc) && d.loc.includes("guardian_email"))
        : null;
      // Only route the 422 to the inline guardian-field error when the
      // guardian field is actually visible (Player + minor). Otherwise the
      // inline DIV isn't mounted and the user would see a silent failure —
      // fall back to the friendly toast instead.
      const guardianFieldVisible = isMinorEligibleProfile && form.is_minor;
      if (guardianPydanticErr && guardianFieldVisible) {
        setGuardianError(guardianMessage());
        requestAnimationFrame(() => {
          const el = guardianRef.current;
          if (el) {
            try { el.focus({ preventScroll: true }); } catch { el.focus(); }
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
      } else {
        toast.error(extractErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      {runtimeCfg.__loaded && !runtimeCfg.public_registration_enabled ? (
        <div className="tsw-section py-16 max-w-2xl" data-testid="register-disabled">
          <div className="tsw-card p-8 sm:p-10 text-center space-y-4 border-amber-300 bg-amber-50">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-200 text-amber-900 text-xl font-bold">⓿</div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-amber-900" data-testid="register-disabled-title">
              {t("demoLockdown.bannerTitle")}
            </h1>
            <p className="text-amber-900/90 text-base" data-testid="register-disabled-body">
              {t("demoLockdown.registerHidden")}
            </p>
            <p className="text-sm text-amber-900/70">
              <Link to="/login" className="underline font-semibold" data-testid="register-disabled-login-link">
                {t("common.login")}
              </Link>
            </p>
          </div>
        </div>
      ) : (
      <div className="tsw-section py-12 max-w-2xl">
        <div className="mb-8">
          <div className="tsw-overline mb-2">{t("common.register")}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">{t("auth.registerTitle")}</h1>
          <p className="text-slate-600 mt-2">{t("auth.registerSub")}</p>
        </div>
        <form onSubmit={submit} className="tsw-card p-6 sm:p-8 space-y-5" data-testid="register-form">
          <div>
            <label className="tsw-label">{t("auth.profile")}</label>
            {isProfileLocked ? (
              <div
                className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3"
                data-testid="register-profile-locked"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-xs font-semibold">
                    ✓
                  </span>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-900 truncate" data-testid="register-profile-locked-label">
                      {tProfile(lockedProfile.key, lockedProfile)}
                    </div>
                    <div className="text-xs text-slate-500">{lockedProfile.key}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Use a hard navigation so the browser honours the
                    // `#profiles` anchor natively (React Router does not
                    // auto-scroll to hash fragments with BrowserRouter).
                    window.location.assign("/#profiles");
                  }}
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800 whitespace-nowrap"
                  data-testid="register-change-profile"
                >
                  {t("auth.changeProfile")}
                </button>
                {/* Hidden field keeps the form submission consistent. */}
                <input type="hidden" name="profile_key" value={form.profile_key} />
              </div>
            ) : (
              <select className="tsw-input" value={form.profile_key} onChange={(e) => handleProfileChange(e.target.value)} required data-testid="register-profile">
                <option value="">{t("phase2.selectProfile")}</option>
                {profiles.map((p) => (
                  <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>
                ))}
              </select>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="tsw-label">{t("auth.fullName")}</label>
              <input className="tsw-input" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="register-name" />
            </div>
            <div>
              <label className="tsw-label">{t("auth.email")}</label>
              <input type="email" className="tsw-input" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="register-email" />
            </div>
            <div>
              <label className="tsw-label">{t("auth.password")}</label>
              <input type="password" className="tsw-input" required minLength={8} value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="register-password" />
            </div>
            <div>
              <label className="tsw-label">{t("auth.country")}</label>
              <select className="tsw-input" value={form.country} onChange={(e) => update("country", e.target.value)} data-testid="register-country">
                <option value="">{t("phase2.selectCountry")}</option>
                {sortedCountries.map((c) => (
                  <option key={c.code} value={c.code}>{`${tCountry(c.code, c)} (${c.code})`}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="tsw-label">{t("auth.language")}</label>
              <select className="tsw-input" value={form.language} onChange={(e) => update("language", e.target.value)} data-testid="register-language">
                {LANGUAGES.filter((L) => L.isActive).map((L) => (
                  <option key={L.code} value={L.code}>{L.nameNative}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-7" data-testid="register-minor-wrapper" hidden={!isMinorEligibleProfile} style={isMinorEligibleProfile ? undefined : { display: "none" }}>
              <input type="checkbox" checked={form.is_minor} onChange={(e) => update("is_minor", e.target.checked)} data-testid="register-minor" disabled={!isMinorEligibleProfile} />
              {t("auth.isMinor")}
            </label>
            {isMinorEligibleProfile && form.is_minor && (
              <div className="sm:col-span-2" data-testid="register-guardian-wrapper">
                <label className="tsw-label">{t("auth.guardianEmail")}</label>
                <input
                  ref={guardianRef}
                  type="email"
                  className={`tsw-input ${guardianError ? "border-red-400 focus:border-red-500 focus:ring-red-200" : ""}`}
                  value={form.guardian_email}
                  onChange={(e) => update("guardian_email", e.target.value)}
                  onBlur={() => {
                    if (form.guardian_email && !isValidEmailShape(form.guardian_email)) {
                      setGuardianError(guardianMessage());
                    }
                  }}
                  aria-invalid={guardianError ? "true" : "false"}
                  aria-describedby={guardianError ? "register-guardian-error" : undefined}
                  data-testid="register-guardian-email"
                />
                {guardianError && (
                  <p
                    id="register-guardian-error"
                    className="mt-1.5 text-sm text-red-600"
                    data-testid="register-guardian-error"
                  >
                    {guardianError}
                  </p>
                )}
              </div>
            )}
          </div>

          {lang === "zh" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="legal-draft-banner">
              {t("auth.legalDraftBanner")}
            </div>
          )}

          {/* PREVIEW-SIGNUP-APP-SELECTION-CHOICE-1 (Feb 2026) —
              Scope selector. Master Rule: Racket Lab → Questionnaires.
              When the RL env gate is off (default), the RL radio is
              disabled and a controlled invite-only message is shown.
              The backend still enforces this defensively. */}
          <div className="space-y-3 pt-2 border-t border-slate-100" data-testid="register-app-selection">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
              {t("auth.appScopeTitle")}
            </div>
            <p className="text-xs text-slate-500">{t("auth.appScopeHelp")}</p>

            <label
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                form.selected_app === "questionnaires"
                  ? "border-emerald-300 bg-emerald-50/60"
                  : "border-slate-200 hover:bg-slate-50/60"
              }`}
              data-testid="register-app-questionnaires-wrapper"
            >
              <input
                type="radio"
                name="register-selected-app"
                className="mt-1"
                value="questionnaires"
                checked={form.selected_app === "questionnaires"}
                onChange={() => update("selected_app", "questionnaires")}
                data-testid="register-app-questionnaires"
              />
              <span className="min-w-0">
                <span className="block font-semibold text-slate-900">
                  {t("auth.appScopeQuestionnaires")}
                </span>
                <span className="text-slate-600 text-sm">
                  {t("auth.appScopeQuestionnairesHelp")}
                </span>
              </span>
            </label>

            {(() => {
              const rlOpen = !!runtimeCfg?.app_gates?.racket_lab;
              const rlDisabled = !rlOpen;
              return (
                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                    rlDisabled
                      ? "border-slate-200 bg-slate-50/60 cursor-not-allowed opacity-80"
                      : form.selected_app === "racket_lab"
                      ? "border-emerald-300 bg-emerald-50/60 cursor-pointer"
                      : "border-slate-200 cursor-pointer hover:bg-slate-50/60"
                  }`}
                  data-testid="register-app-racket-lab-wrapper"
                >
                  <input
                    type="radio"
                    name="register-selected-app"
                    className="mt-1"
                    value="racket_lab"
                    checked={form.selected_app === "racket_lab"}
                    onChange={() => update("selected_app", "racket_lab")}
                    disabled={rlDisabled}
                    data-testid="register-app-racket-lab"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center flex-wrap gap-2">
                      <span className="font-semibold text-slate-900">
                        {t("auth.appScopeRacketLab")}
                      </span>
                      {rlDisabled && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5"
                          data-testid="register-app-racket-lab-status-chip"
                        >
                          {t("auth.appScopeInviteOnly")}
                        </span>
                      )}
                    </span>
                    <span className="block text-slate-600 text-sm mt-0.5">
                      {rlDisabled
                        ? t("auth.appScopeRacketLabBlockedHelp")
                        : t("auth.appScopeRacketLabOpenHelp")}
                    </span>
                    {!rlDisabled && form.selected_app === "racket_lab" && (
                      <span
                        className="block text-emerald-700 text-xs mt-1.5 font-medium"
                        data-testid="register-app-racket-lab-auto-include"
                      >
                        ↳ {t("auth.appScopeRacketLabAutoInclude")}
                      </span>
                    )}
                  </span>
                </label>
              );
            })()}
          </div>

          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">{t("phase2.requiredConsents")}</div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={form.consent_terms} onChange={(e) => update("consent_terms", e.target.checked)} data-testid="register-consent-terms" />
              <span>{t("auth.consentTerms")} <span className="text-rose-500">*</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={form.consent_privacy} onChange={(e) => update("consent_privacy", e.target.checked)} data-testid="register-consent-privacy" />
              <span>{t("auth.consentPrivacy")} <span className="text-rose-500">*</span></span>
            </label>

            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 pt-3">{t("phase2.optionalConsents")}</div>
            <p className="text-xs text-slate-500">{t("auth.consentSeparationNote")}</p>

            <label className="flex items-start gap-2 text-sm text-slate-700 rounded-lg border border-slate-100 p-3 hover:bg-slate-50/60">
              <input type="checkbox" className="mt-1" checked={form.research_consent} onChange={(e) => update("research_consent", e.target.checked)} data-testid="register-consent-research" />
              <span>
                <span className="block font-semibold text-slate-900">{t("auth.research_consent_title")}</span>
                <span className="text-slate-600">{t("auth.research_consent")}</span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-slate-700 rounded-lg border border-slate-100 p-3 hover:bg-slate-50/60">
              <input type="checkbox" className="mt-1" checked={form.crm_contact_consent} onChange={(e) => update("crm_contact_consent", e.target.checked)} data-testid="register-consent-crm" />
              <span>
                <span className="block font-semibold text-slate-900">{t("auth.crm_contact_consent_title")}</span>
                <span className="text-slate-600">{t("auth.crm_contact_consent")}</span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-slate-700 rounded-lg border border-slate-100 p-3 hover:bg-slate-50/60">
              <input type="checkbox" className="mt-1" checked={form.commercial_communications_consent} onChange={(e) => update("commercial_communications_consent", e.target.checked)} data-testid="register-consent-commercial" />
              <span>
                <span className="block font-semibold text-slate-900">{t("auth.commercial_consent_title")}</span>
                <span className="text-slate-600">{t("auth.commercial_consent")}</span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-slate-700 rounded-lg border border-slate-100 p-3 hover:bg-slate-50/60">
              <input type="checkbox" className="mt-1" checked={form.pilot_participation_consent} onChange={(e) => update("pilot_participation_consent", e.target.checked)} data-testid="register-consent-pilot" />
              <span>
                <span className="block font-semibold text-slate-900">{t("auth.pilot_consent_title")}</span>
                <span className="text-slate-600">{t("auth.pilot_consent")}</span>
              </span>
            </label>
          </div>

          <button type="submit" className="tsw-btn-primary w-full" disabled={loading} data-testid="register-submit">
            {loading ? t("common.saving") : t("auth.submitRegister")}
          </button>
          <div className="text-center text-sm text-slate-500">
            {t("auth.haveAccount")} <Link to="/login" className="text-emerald-700 font-medium" data-testid="register-to-login">{t("auth.submitLogin")}</Link>
          </div>
        </form>
      </div>
      )}
    </PublicLayout>
  );
};

export default Register;
